import { PenSquare, RefreshCw } from "lucide-react";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import { parseReportCitations } from "@/utils/parseCitations";
import { useDocumentViewer } from "@/contexts/DocumentViewerContext";
import type { Document } from "@/types/models";
import remarkGfm from "remark-gfm";
import remarkSupersub from "remark-supersub";
import rehypeRaw from "rehype-raw";
import rehypeExternalLinks from "rehype-external-links";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MarkdownEditor } from "./MarkdownEditor";

export interface Citation {
  index: number;
  label: string;
  filename?: string;
  page?: string;
  sourceText?: string;
}

export interface GeneratedContent {
  text: string;
  citations: Citation[];
}

/** A section's artefacts, rendered below its text from the section's current
 *  sources. Never part of the saved text, so never in the editor. */
export interface ArtifactAppendix {
  markdown: string;
  isLoading: boolean;
  /** Artefact names, for plain-text copies. */
  names: string[];
}

interface GeneratedContentViewProps {
  content: GeneratedContent;
  artifactAppendix?: ArtifactAppendix;
  onRegenerate: () => void;
  onEdit: () => void;
  isRegenerating?: boolean;
  /** Hide the built-in Regenerate/Edit toolbar when the parent renders its own. */
  hideToolbar?: boolean;
  /** Controlled edit mode — show MDXEditor instead of the read view. */
  isEditing?: boolean;
  /** Called with the new markdown text when the user saves. */
  onSaveEdit?: (newText: string) => void;
  /** Called when the user cancels editing. */
  onCancelEdit?: () => void;
  /** Project documents — enables citation badges to open the document viewer on click. */
  documents?: Document[];
}

/**
 * Renders the AI-generated content for a section.
 * Parses `{n}` citation refs in the text and renders citation badges at the bottom.
 * When `isEditing` is true, shows an inline MDXEditor with Save/Cancel controls.
 */
export const GeneratedContentView = ({
  content,
  artifactAppendix,
  onRegenerate,
  onEdit,
  isRegenerating = false,
  hideToolbar = false,
  isEditing = false,
  onSaveEdit,
  onCancelEdit,
  documents,
}: GeneratedContentViewProps) => {
  const { openDocument } = useDocumentViewer();

  return (
    <div className="markdown-content flex flex-col gap-3">
      {/* Toolbar */}
      {!hideToolbar && !isEditing && (
        <div className="flex items-center justify-end gap-2">
          <Button size="sm" variant="ghost" className="gap-1" onClick={onRegenerate} disabled={isRegenerating}>
            <RefreshCw className={`w-3 h-3 ${isRegenerating ? "animate-spin" : ""}`} />
            Regenerate
          </Button>
          <Button size="sm" variant="ghost" className="gap-1" onClick={onEdit}>
            <PenSquare className="w-3 h-3" />
            Edit
          </Button>
        </div>
      )}

      {/* Content body — editor or read view */}
      {isEditing ? (
        <>
          <MarkdownEditor
            markdown={content.text}
            onSave={(text) => onSaveEdit?.(text)}
            onCancel={() => onCancelEdit?.()}
          />
          {/* Artefacts stay read-only below the editor — they follow the section's sources. */}
          {artifactAppendix?.markdown && (
            <div className="text-sm text-foreground leading-relaxed prose prose-sm max-w-none">
              <MarkdownContent text={artifactAppendix.markdown} />
            </div>
          )}
        </>
      ) : (
        <div className="text-sm text-foreground leading-relaxed prose prose-sm max-w-none" data-markdown-body>
          <MarkdownContent text={parseReportCitations(content.text).text} />
          {artifactAppendix?.markdown && <MarkdownContent text={artifactAppendix.markdown} />}
        </div>
      )}
      {artifactAppendix?.isLoading && (
        <p className="text-xs text-muted-foreground">Loading artefacts…</p>
      )}

      {/* Citation badges */}
      {!isEditing && content.citations.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {content.citations.map((c) => {
            const doc = c.filename ? documents?.find((d) => d.filename === c.filename) : undefined;
            const clickable = !!doc;
            return (
              <Badge
                key={c.index}
                as={clickable ? "button" : "div"}
                type={clickable ? "button" : undefined}
                variant="brand"
                appearance="outlined"
                className={`bg-brand-teal/10 border-none font-normal ${clickable ? "cursor-pointer hover:bg-brand-teal/20" : ""}`}
                onClick={clickable ? () => {
                  openDocument({ doc: doc!, page: c.page, sourceText: c.sourceText, side: "left", showTabs: true });
                } : undefined}
              >
                [{c.index}] {c.label}
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
};

/** Artefact images and charts are rendered as data URLs, which react-markdown
 *  strips by default. Only image data in an image source is let through — charts
 *  are stored as SVG, which is safe here because an <img> never runs SVG scripts. */
const allowEmbeddedImages = (url: string, key: string): string =>
  key === "src" && /^data:image\/(png|jpe?g|gif|webp|bmp|svg\+xml);base64,/i.test(url) ? url : defaultUrlTransform(url);

/** Renders markdown text. Citation markers (`^n^`) are rendered as superscripts via remark-supersub. */
const MarkdownContent = ({ text }: { text: string }) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm, remarkSupersub]}
    rehypePlugins={[rehypeRaw, [rehypeExternalLinks, { target: "_blank", rel: ["noopener", "noreferrer"] }]]}
    urlTransform={allowEmbeddedImages}
  >
    {text}
  </ReactMarkdown>
);
